import {
  accountMutationHeaders,
  readBoundedAccountJson,
} from "./account-state-session-repository.js";
import {
  createAccountOperationId,
  isAccountOperationId,
} from "./account-operation-id.js";

const SCHEMA_VERSION = 1;
const RESPONSE_MAX_BYTES = 8 * 1024;
const ACCOUNT_PATH = "/api/v1/account/language-preferences";
const LANGUAGE_CONFIG_BRAND = Symbol.for("fishystuff.language-config-contract.v1");
const DATA_LANGUAGE_PATTERN = /^[a-z0-9_]{1,32}$/u;
const VALUE_FIELDS = Object.freeze([
  "locale",
  "dataLanguage",
  "serverDataspace",
]);
const COMMAND_FIELDS = Object.freeze({
  create: ["kind", "schemaVersion", "operationId", "values"],
  set: [
    "kind",
    "schemaVersion",
    "operationId",
    "expectedRevision",
    "values",
  ],
  reset: ["kind", "schemaVersion", "operationId", "expectedRevision"],
});
const DEFAULT_VALUES = Object.freeze({
  locale: null,
  dataLanguage: null,
  serverDataspace: null,
});

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasExactFields(value, expectedFields) {
  if (!isRecord(value)) return false;
  const actualFields = Object.keys(value).sort();
  const sortedExpected = expectedFields.slice().sort();
  return actualFields.length === sortedExpected.length
    && actualFields.every((field, index) => field === sortedExpected[index]);
}

function positiveSafeInteger(value) {
  return Number.isSafeInteger(value) && value > 0;
}

function requireLanguageConfig(languageConfig) {
  const contract = languageConfig ?? globalThis.__fishystuffLanguageConfigV1;
  if (
    contract?.[LANGUAGE_CONFIG_BRAND] !== SCHEMA_VERSION
    || typeof contract.regionDefaultValue !== "string"
    || contract.regionDefaultValue.length === 0
    || typeof contract.isLocale !== "function"
    || typeof contract.isDataLanguage !== "function"
    || typeof contract.isServerDataspace !== "function"
  ) {
    throw new TypeError(
      "Language preferences require the branded language configuration contract.",
    );
  }
  return contract;
}

function isDataLanguage(value, languageConfig) {
  return typeof value === "string"
    && (
      value === languageConfig.regionDefaultValue
      || (
        DATA_LANGUAGE_PATTERN.test(value)
        && languageConfig.isDataLanguage(value)
      )
    );
}

export function decodeLanguagePreferenceValuesV1(value, languageConfig) {
  const contract = requireLanguageConfig(languageConfig);
  if (
    !hasExactFields(value, VALUE_FIELDS)
    || (value.locale !== null && !contract.isLocale(value.locale))
    || (
      value.dataLanguage !== null
      && !isDataLanguage(value.dataLanguage, contract)
    )
    || (
      value.serverDataspace !== null
      && !contract.isServerDataspace(value.serverDataspace)
    )
  ) {
    throw new TypeError("Account language preference values are invalid.");
  }
  return Object.freeze({
    locale: value.locale,
    dataLanguage: value.dataLanguage,
    serverDataspace: value.serverDataspace,
  });
}

function freezeProjection(value, languageConfig) {
  return value.kind === "missing"
    ? Object.freeze({ kind: "missing", schemaVersion: SCHEMA_VERSION })
    : Object.freeze({
      kind: "ready",
      schemaVersion: SCHEMA_VERSION,
      revision: value.revision,
      values: decodeLanguagePreferenceValuesV1(value.values, languageConfig),
    });
}

function freezeState(value, languageConfig) {
  const next = { ...value };
  if (next.authoritativeState) {
    next.authoritativeState = freezeProjection(
      next.authoritativeState,
      languageConfig,
    );
  }
  return Object.freeze(next);
}

export function decodeLanguagePreferencesProjectionV1(value, languageConfig) {
  const contract = requireLanguageConfig(languageConfig);
  if (
    hasExactFields(value, ["kind", "schemaVersion"])
    && value.kind === "missing"
    && value.schemaVersion === SCHEMA_VERSION
  ) {
    return freezeProjection(value, contract);
  }
  if (
    !hasExactFields(value, ["kind", "schemaVersion", "revision", "values"])
    || value.kind !== "ready"
    || value.schemaVersion !== SCHEMA_VERSION
    || !positiveSafeInteger(value.revision)
  ) {
    throw new TypeError("Account language preferences projection is invalid.");
  }
  return freezeProjection(value, contract);
}

export function decodeLanguagePreferencesCommandV1(value, languageConfig) {
  const contract = requireLanguageConfig(languageConfig);
  const expectedFields = COMMAND_FIELDS[value?.kind];
  if (
    !expectedFields
    || !hasExactFields(value, expectedFields)
    || value.schemaVersion !== SCHEMA_VERSION
    || !isAccountOperationId(value.operationId)
    || (
      ["set", "reset"].includes(value.kind)
      && !positiveSafeInteger(value.expectedRevision)
    )
  ) {
    throw new TypeError("Account language preferences command is invalid.");
  }
  return Object.freeze({
    ...value,
    ...(["create", "set"].includes(value.kind)
      ? { values: decodeLanguagePreferenceValuesV1(value.values, contract) }
      : {}),
  });
}

export function decodeLanguagePreferencesAcknowledgementV1(
  value,
  operationId,
  languageConfig,
) {
  const contract = requireLanguageConfig(languageConfig);
  if (
    !hasExactFields(value, ["kind", "operationId", "authoritativeState"])
    || !["applied", "conflict"].includes(value.kind)
    || value.operationId !== operationId
  ) {
    throw new TypeError("Account language preferences acknowledgement is invalid.");
  }
  return Object.freeze({
    kind: value.kind,
    operationId,
    authoritativeState:
      decodeLanguagePreferencesProjectionV1(value.authoritativeState, contract),
  });
}

function accountUrl(apiBaseUrl) {
  let base;
  try {
    base = new URL(String(apiBaseUrl ?? ""));
  } catch (_error) {
    throw new TypeError("Language preferences API base URL is invalid.");
  }
  if (
    !["http:", "https:"].includes(base.protocol)
    || base.username
    || base.password
    || base.pathname !== "/"
    || base.search
    || base.hash
  ) {
    throw new TypeError("Language preferences API base URL must be an HTTP origin.");
  }
  return `${base.origin}${ACCOUNT_PATH}`;
}

export function createLanguagePreferencesAccountProvider({
  apiBaseUrl,
  fetchImpl = globalThis.fetch,
  languageConfig,
} = {}) {
  if (typeof fetchImpl !== "function") {
    throw new TypeError("Language preferences provider requires fetch.");
  }
  const url = accountUrl(apiBaseUrl);
  const contract = requireLanguageConfig(languageConfig);

  async function load({ signal } = {}) {
    const response = await fetchImpl(url, {
      method: "GET",
      cache: "no-store",
      credentials: "include",
      headers: { Accept: "application/json" },
      redirect: "error",
      signal,
    });
    if (response.status === 401) {
      return freezeState({ kind: "signedOut" }, contract);
    }
    if (!response.ok) throw new Error("Language preferences request failed.");
    return decodeLanguagePreferencesProjectionV1(
      await readBoundedAccountJson(
        response,
        "Account language preferences",
        RESPONSE_MAX_BYTES,
      ),
      contract,
    );
  }

  async function mutate(session, command, { signal } = {}) {
    const exactCommand = decodeLanguagePreferencesCommandV1(command, contract);
    const response = await fetchImpl(url, {
      method: "POST",
      cache: "no-store",
      credentials: "include",
      headers: accountMutationHeaders(session),
      redirect: "error",
      signal,
      body: JSON.stringify(exactCommand),
    });
    if (response.status === 401) {
      return freezeState({ kind: "signedOut" }, contract);
    }
    if (![200, 409].includes(response.status)) {
      throw new Error("Language preferences mutation failed.");
    }
    const acknowledgement = decodeLanguagePreferencesAcknowledgementV1(
      await readBoundedAccountJson(
        response,
        "Account language preferences acknowledgement",
        RESPONSE_MAX_BYTES,
      ),
      exactCommand.operationId,
      contract,
    );
    if (
      (response.status === 200 && acknowledgement.kind !== "applied")
      || (response.status === 409 && acknowledgement.kind !== "conflict")
    ) {
      throw new TypeError(
        "Language preferences status and acknowledgement disagree.",
      );
    }
    return acknowledgement;
  }

  return Object.freeze({ load, mutate });
}

function hasMethods(value, names) {
  return names.every((name) => typeof value?.[name] === "function");
}

function expectedRevision(options, label) {
  if (options === undefined) return undefined;
  if (
    !isRecord(options)
    || Object.keys(options).some((field) => field !== "expectedRevision")
  ) {
    throw new TypeError(`${label} options contain unsupported fields.`);
  }
  if (options.expectedRevision === undefined || options.expectedRevision === null) {
    return options.expectedRevision;
  }
  if (!positiveSafeInteger(options.expectedRevision)) {
    throw new TypeError(`${label} expected revision is invalid.`);
  }
  return options.expectedRevision;
}

function valuesEqual(left, right) {
  return VALUE_FIELDS.every((field) => left[field] === right[field]);
}

export function createAuthenticatedLanguagePreferencesRepository({
  sessionRepository,
  accountProvider = null,
  apiBaseUrl,
  fetchImpl = globalThis.fetch,
  createOperationId = createAccountOperationId,
  languageConfig,
} = {}) {
  if (!hasMethods(sessionRepository, ["load", "refresh", "state", "subscribe"])) {
    throw new TypeError("Language preferences require the account session repository.");
  }
  const contract = requireLanguageConfig(languageConfig);
  const remote = accountProvider ?? createLanguagePreferencesAccountProvider({
    apiBaseUrl,
    fetchImpl,
    languageConfig: contract,
  });
  if (!hasMethods(remote, ["load", "mutate"]) || typeof createOperationId !== "function") {
    throw new TypeError("Language preferences require their typed account provider.");
  }

  let currentState = freezeState({ kind: "uninitialized" }, contract);
  let authoritativeState = null;
  let accountId = null;
  let requestId = 0;
  let requestController = null;
  let sessionLoadDepth = 0;
  let disposed = false;
  const listeners = new Set();

  function state() {
    return currentState;
  }

  function deliver(listener) {
    try {
      listener(currentState);
    } catch (_error) {
      globalThis.reportError?.(new Error("Language preferences subscriber failed."));
    }
  }

  function publish(value) {
    if (disposed) return currentState;
    const nextState = freezeState(value, contract);
    if (JSON.stringify(currentState) === JSON.stringify(nextState)) return currentState;
    currentState = nextState;
    for (const listener of listeners) deliver(listener);
    return currentState;
  }

  function beginRequest({ loading = false } = {}) {
    requestId += 1;
    requestController?.abort();
    requestController = new AbortController();
    if (loading) publish({ kind: "loading" });
    return Object.freeze({ id: requestId, controller: requestController });
  }

  function isCurrent(request) {
    return !disposed
      && request.id === requestId
      && !request.controller.signal.aborted;
  }

  function clearAuthority(value) {
    authoritativeState = null;
    accountId = null;
    return publish(value);
  }

  function applyProjection(projection, nextAccountId) {
    authoritativeState = projection;
    accountId = nextAccountId;
    return publish({ kind: "ready", authoritativeState: projection });
  }

  function unavailable(operation, reason) {
    return clearAuthority({ kind: "unavailable", operation, reason });
  }

  async function freshSession(request, { refresh = false } = {}) {
    sessionLoadDepth += 1;
    try {
      const session = await sessionRepository[refresh ? "refresh" : "load"]();
      return isCurrent(request) ? session : null;
    } finally {
      sessionLoadDepth -= 1;
    }
  }

  function applySessionBoundary(session) {
    if (session?.kind === "signedOut") {
      return clearAuthority({ kind: "signedOut" });
    }
    if (session?.kind === "locked") {
      return clearAuthority({
        kind: "locked",
        reason: session.reason || "account-locked",
      });
    }
    if (session?.kind === "unavailable") {
      return unavailable("account", session.reason || "session-request-failed");
    }
    if (session?.kind !== "ready") {
      return clearAuthority({ kind: "loading" });
    }
    return null;
  }

  async function readRemote(request, session) {
    try {
      const projection = await remote.load({ signal: request.controller.signal });
      if (!isCurrent(request)) return state();
      if (projection.kind === "signedOut") {
        const refreshed = await freshSession(request, { refresh: true });
        if (!refreshed || !isCurrent(request)) return state();
        if (applySessionBoundary(refreshed) || refreshed.kind !== "ready") return state();
        return unavailable("load", "account-authorization-rejected");
      }
      return applyProjection(projection, session.accountId);
    } catch (error) {
      if (!isCurrent(request) || error?.name === "AbortError") return state();
      return unavailable("load", "language-request-failed");
    }
  }

  async function load() {
    if (disposed) return state();
    const request = beginRequest({ loading: true });
    const session = await freshSession(request);
    if (!session || !isCurrent(request)) return state();
    if (applySessionBoundary(session) || session.kind !== "ready") return state();
    return readRemote(request, session);
  }

  function operationId() {
    const value = createOperationId();
    if (!isAccountOperationId(value)) {
      throw new TypeError("Language preferences operation ID is invalid.");
    }
    return value;
  }

  function currentValues() {
    return authoritativeState?.kind === "ready"
      ? authoritativeState.values
      : DEFAULT_VALUES;
  }

  function commandFor(nextValues, reset) {
    if (!authoritativeState) return null;
    if (reset) {
      return authoritativeState.kind === "missing"
        ? null
        : decodeLanguagePreferencesCommandV1({
          kind: "reset",
          schemaVersion: SCHEMA_VERSION,
          operationId: operationId(),
          expectedRevision: authoritativeState.revision,
        }, contract);
    }
    return decodeLanguagePreferencesCommandV1({
      kind: authoritativeState.kind === "missing" ? "create" : "set",
      schemaVersion: SCHEMA_VERSION,
      operationId: operationId(),
      ...(authoritativeState.kind === "ready"
        ? { expectedRevision: authoritativeState.revision }
        : {}),
      values: nextValues,
    }, contract);
  }

  function conflict(operation, expected) {
    return publish({
      kind: "conflict",
      operation,
      expectedRevision: expected,
      authoritativeState,
    });
  }

  async function mutate(operation, nextValues, options, { reset = false } = {}) {
    const expected = expectedRevision(options, `Language ${operation}`);
    if (currentState.kind === "pending") return state();
    if (!authoritativeState || !accountId) return state();
    const currentRevision = authoritativeState.kind === "ready"
      ? authoritativeState.revision
      : null;
    if (!reset && valuesEqual(currentValues(), nextValues)) return state();
    if (expected !== undefined && expected !== currentRevision) {
      return conflict(operation, expected);
    }
    const command = commandFor(nextValues, reset);
    if (!command) return state();
    const startingAccountId = accountId;
    const request = beginRequest();
    publish({ kind: "pending", operation, authoritativeState });

    try {
      const session = await freshSession(request, { refresh: true });
      if (!session || !isCurrent(request)) return state();
      if (applySessionBoundary(session) || session.kind !== "ready") return state();
      if (session.accountId !== startingAccountId) {
        return unavailable(operation, "account-authority-changed");
      }
      const acknowledgement = await remote.mutate(session, command, {
        signal: request.controller.signal,
      });
      if (!isCurrent(request)) return state();
      if (acknowledgement.kind === "signedOut") {
        const refreshed = await freshSession(request, { refresh: true });
        if (!refreshed || !isCurrent(request)) return state();
        if (applySessionBoundary(refreshed) || refreshed.kind !== "ready") return state();
        return unavailable(operation, "account-authorization-rejected");
      }
      authoritativeState = acknowledgement.authoritativeState;
      accountId = session.accountId;
      if (acknowledgement.kind === "conflict") {
        return publish({
          kind: "conflict",
          operation,
          expectedRevision: currentRevision,
          authoritativeState,
        });
      }
      return publish({ kind: "ready", authoritativeState });
    } catch (error) {
      if (!isCurrent(request) || error?.name === "AbortError") return state();
      return unavailable(operation, "language-mutation-failed");
    }
  }

  function setPreferences(values, options) {
    return mutate(
      "setPreferences",
      decodeLanguagePreferenceValuesV1(values, contract),
      options,
    );
  }

  function updateField(field, value, options) {
    const nextValues = decodeLanguagePreferenceValuesV1({
      ...currentValues(),
      [field]: value,
    }, contract);
    return mutate(`set${field[0].toUpperCase()}${field.slice(1)}`, nextValues, options);
  }

  function setLocale(locale, options) {
    return updateField("locale", locale, options);
  }

  function setDataLanguage(dataLanguage, options) {
    return updateField("dataLanguage", dataLanguage, options);
  }

  function setServerDataspace(serverDataspace, options) {
    return updateField("serverDataspace", serverDataspace, options);
  }

  function reset(options) {
    return mutate("reset", DEFAULT_VALUES, options, { reset: true });
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Language preferences listener must be a function.");
    }
    if (disposed) return () => {};
    listeners.add(listener);
    deliver(listener);
    return () => listeners.delete(listener);
  }

  const unsubscribeSession = sessionRepository.subscribe((session) => {
    if (disposed || sessionLoadDepth > 0) return;
    if (session.kind !== "ready") {
      beginRequest();
      applySessionBoundary(session);
      return;
    }
    const request = beginRequest({ loading: true });
    void readRemote(request, session);
  });

  function dispose() {
    if (disposed) return;
    disposed = true;
    requestId += 1;
    requestController?.abort();
    requestController = null;
    listeners.clear();
    unsubscribeSession();
  }

  return Object.freeze({
    dispose,
    load,
    reset,
    setDataLanguage,
    setLocale,
    setPreferences,
    setServerDataspace,
    state,
    subscribe,
  });
}
